import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  useAccordionContext,
  useAccordionItemContext,
} from '@reach/accordion';
import cx from 'clsx';
import React from 'react';

import commonStyles from '@/components/Copy/Common.module.scss';
import { MinusIcon, PlusIcon } from '@/components/icons';
import { shouldBeExpandedByDefault } from '@/utils/copy/shouldBeExpandedByDefault';

import styles from './CopyParagraph.module.scss';

interface CopyParagraphPropTypes {
  heading: string;
  copy: string;
  expand?: boolean;
  visible?: boolean;
}

const CopyParagraph: React.FC<CopyParagraphPropTypes> = ({
  copy,
  heading,
  expand = false,
  visible = true,
}) => {
  if (!visible || !heading || !copy) return null;

  const AnimatedIcon = () => {
    const { isExpanded } = useAccordionItemContext();
    const { openPanels } = useAccordionContext();
    const loadingAnimation = isExpanded && openPanels.length > 0;

    return (
      <>
        <MinusIcon
          className={cx(
            styles.accordionIcon,
            loadingAnimation ? styles.expandMinus : styles.hideMinus
          )}
        />
        <PlusIcon
          className={cx(
            styles.accordionIcon,
            loadingAnimation ? styles.collapsePlus : styles.showPlus
          )}
        />
      </>
    );
  };

  const AnimatedAccordionPanel = ({
    children,
  }: {
    children: React.ReactNode;
  }) => {
    const { isExpanded } = useAccordionItemContext();

    return (
      <AccordionPanel
        hidden={false}
        aria-hidden={!isExpanded || undefined}
        className={cx(
          commonStyles.accordionPanel,
          isExpanded && commonStyles.expand
        )}
        data-testid="copy-paragraph-content"
      >
        {children}
      </AccordionPanel>
    );
  };

  return (
    <div className={styles.layout} data-testid="copy-paragraph-wrapper">
      <Accordion
        collapsible
        defaultIndex={shouldBeExpandedByDefault(expand, 0)}
      >
        <AccordionItem>
          <AccordionButton
            className={cx(commonStyles.accordionButton, styles.headingWrapper)}
          >
            <h1 className={styles.heading}>{heading}</h1>
            <AnimatedIcon />
          </AccordionButton>
          <AnimatedAccordionPanel>
            <div
              className={cx(commonStyles.styles, styles.additionalStyles)}
              dangerouslySetInnerHTML={{
                __html: copy,
              }}
            />
          </AnimatedAccordionPanel>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default CopyParagraph;
